import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { trpc } from '../../lib/trpc';
import { useCurrency } from '../../hooks/useCurrency';
import type { CurrencyCode } from '../../lib/currency';

interface SubscriptionStatusProps {
  subscription: {
    id: string | null;
    plan: 'FREE' | 'PRO' | 'ENTERPRISE';
    status: 'ACTIVE' | 'CANCELED' | 'PAST_DUE' | 'UNPAID' | 'TRIALING' | null;
    billingInterval: 'MONTHLY' | 'YEARLY' | null;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    // Trial information
    isOnTrial?: boolean;
    trialEndsAt?: string | null;
    trialDaysRemaining?: number | null;
  };
  onRefresh: () => void;
  onSubscribe?: (interval: 'monthly' | 'yearly', currency: CurrencyCode) => void;
  isSubscribeLoading?: boolean;
}

/**
 * Get status chip color and label
 */
function getStatusInfo(
  status: string | null,
  cancelAtPeriodEnd: boolean,
  isOnTrial?: boolean
): { color: 'success' | 'warning' | 'error' | 'default' | 'info'; label: string } {
  if (cancelAtPeriodEnd) {
    return { color: 'warning', label: 'Canceling' };
  }

  // Show trial status if user is on a free trial
  if (isOnTrial) {
    return { color: 'info', label: 'Free Trial' };
  }

  switch (status) {
    case 'ACTIVE':
      return { color: 'success', label: 'Active' };
    case 'TRIALING':
      return { color: 'success', label: 'Trial' };
    case 'PAST_DUE':
      return { color: 'error', label: 'Past Due' };
    case 'UNPAID':
      return { color: 'error', label: 'Unpaid' };
    case 'CANCELED':
      return { color: 'default', label: 'Canceled' };
    default:
      return { color: 'default', label: 'Free' };
  }
}

/**
 * Format date for display
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Subscription Status Component
 * Shows current subscription details and management actions
 */
export function SubscriptionStatus({ subscription, onRefresh, onSubscribe, isSubscribeLoading }: SubscriptionStatusProps) {
  const utils = trpc.useUtils();
  const { currency } = useCurrency();

  // Cancel subscription mutation
  const cancelMutation = trpc.billing.cancelSubscription.useMutation({
    onSuccess: () => {
      utils.billing.getSubscription.invalidate();
      onRefresh();
    },
  });

  // Resume subscription mutation
  const resumeMutation = trpc.billing.resumeSubscription.useMutation({
    onSuccess: () => {
      utils.billing.getSubscription.invalidate();
      onRefresh();
    },
  });

  // Create portal session mutation
  const portalMutation = trpc.billing.createPortalSession.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
  });

  const statusInfo = getStatusInfo(subscription.status, subscription.cancelAtPeriodEnd, subscription.isOnTrial);
  const isPro = subscription.plan === 'PRO';
  const hasActiveSubscription = subscription.status === 'ACTIVE' || subscription.status === 'TRIALING';
  const isOnTrial = subscription.isOnTrial ?? false;

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Current Subscription
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={subscription.plan}
            color={isPro ? 'secondary' : 'default'}
            sx={{ fontWeight: 600 }}
          />
          {subscription.status && (
            <Chip
              label={statusInfo.label}
              color={statusInfo.color}
              variant="outlined"
              size="small"
            />
          )}
        </Box>
      </Box>

      {/* Cancellation Warning */}
      {subscription.cancelAtPeriodEnd && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Your subscription is set to cancel at the end of the current billing period on{' '}
          <strong>{formatDate(subscription.currentPeriodEnd)}</strong>. You will be downgraded to
          the Free plan after this date.
        </Alert>
      )}

      {/* Past Due Warning */}
      {subscription.status === 'PAST_DUE' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Your payment is past due. Please update your payment method to continue using Pro features.
        </Alert>
      )}

      {/* Trial Information */}
      {isOnTrial && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon fontSize="small" />
            <Typography variant="body2">
              <strong>You're on a 14-day free trial!</strong> You have{' '}
              <strong>{subscription.trialDaysRemaining} day{subscription.trialDaysRemaining !== 1 ? 's' : ''}</strong>{' '}
              remaining. Add a payment method to continue using Pro features after your trial ends.
              {subscription.trialEndsAt && (
                <> Trial ends on <strong>{formatDate(subscription.trialEndsAt)}</strong>.</>
              )}
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Subscription Details */}
      {isPro && subscription.billingInterval && !isOnTrial && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCardIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Billing Interval
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {subscription.billingInterval === 'MONTHLY' ? 'Monthly' : 'Yearly'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Current Period
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
              </Typography>
            </Box>
          </Box>

          {!subscription.cancelAtPeriodEnd && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Next Billing Date
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatDate(subscription.currentPeriodEnd)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Trial Details */}
      {isOnTrial && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Trial Ends
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatDate(subscription.trialEndsAt ?? null)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Days Remaining
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {subscription.trialDaysRemaining} day{subscription.trialDaysRemaining !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Free Plan Message */}
      {!isPro && !isOnTrial && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          You are currently on the Free plan. Upgrade to Pro to unlock faster monitoring intervals,
          SMS notifications, and more features.
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* Subscribe Now Button - for trial users */}
        {isOnTrial && onSubscribe && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={isSubscribeLoading ? <CircularProgress size={20} color="inherit" /> : <WorkspacePremiumIcon />}
            onClick={() => onSubscribe('monthly', currency)}
            disabled={isSubscribeLoading ?? false}
          >
            {isSubscribeLoading ? 'Processing...' : 'Subscribe Now'}
          </Button>
        )}

        {/* Manage Billing Button - only for users with active Stripe subscription (not trial users) */}
        {hasActiveSubscription && !isOnTrial && (
          <Button
            variant="outlined"
            startIcon={portalMutation.isLoading ? <CircularProgress size={20} /> : <OpenInNewIcon />}
            onClick={() => portalMutation.mutate()}
            disabled={portalMutation.isLoading}
          >
            Manage Billing
          </Button>
        )}

        {/* Cancel/Resume Buttons - only for paid subscriptions (not trial users) */}
        {hasActiveSubscription && !subscription.cancelAtPeriodEnd && !isOnTrial && (
          <Button
            variant="outlined"
            color="error"
            startIcon={cancelMutation.isLoading ? <CircularProgress size={20} /> : <CancelIcon />}
            onClick={() => {
              if (window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.')) {
                cancelMutation.mutate();
              }
            }}
            disabled={cancelMutation.isLoading}
          >
            Cancel Subscription
          </Button>
        )}

        {subscription.cancelAtPeriodEnd && (
          <Button
            variant="contained"
            color="primary"
            startIcon={resumeMutation.isLoading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
            onClick={() => resumeMutation.mutate()}
            disabled={resumeMutation.isLoading}
          >
            Resume Subscription
          </Button>
        )}
      </Box>

      {/* Error Messages */}
      {cancelMutation.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {cancelMutation.error.message}
        </Alert>
      )}
      {resumeMutation.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {resumeMutation.error.message}
        </Alert>
      )}
      {portalMutation.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {portalMutation.error.message}
        </Alert>
      )}
    </Paper>
  );
}

export default SubscriptionStatus;
